import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building, Users, Award, CheckCircle, MapPin } from 'lucide-react';
import { useData } from '../../context/DataContext';
import YouTubeEmbed from '../common/YouTubeEmbed';

const AboutPage = () => {
  const { siteSettings, isLoading } = useData();

  const whatsappUrl = `https://wa.me/${siteSettings?.whatsappNumber || '905546500984'}?text=${encodeURIComponent('Merhaba Taşdemir İnşaat, şirketiniz hakkında bilgi almak istiyorum.')}`;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  const values = [
    {
      icon: CheckCircle,
      title: 'Kalite',
      description: 'Her projemizde en yüksek kalite standartlarını uyguluyoruz.',
    },
    {
      icon: Users,
      title: 'Güven',
      description: 'Müşterilerimizle kurduğumuz güven ilişkisi en büyük varlığımızdır.',
    },
    {
      icon: Award,
      title: 'Tecrübe',
      description: '20 yılı aşkın tecrübemizle sektörün öncüsüyüz.',
    },
    {
      icon: Building,
      title: 'Yenilikçilik',
      description: 'Modern mimari ve teknolojileri projelerimize entegre ediyoruz.',
    },
  ];

  return (
    <div className="min-h-screen bg-anthracite">
      {/* Hero */}
      <section className="relative py-24 bg-anthracite-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="text-gold text-sm font-semibold tracking-widest uppercase">
              Biz Kimiz
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-4">
              Hakkımızda
            </h1>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              {siteSettings?.slogan}
            </p>
            <div className="w-24 h-1 bg-gold mx-auto mt-6" />
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80"
                alt="Taşdemir İnşaat"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-gold/10 rounded-lg -z-10" />
              <div className="absolute -top-8 -left-8 w-32 h-32 border border-gold/30 rounded-lg -z-10" />
            </div>
            <div>
              <span className="text-gold text-sm font-semibold tracking-widest uppercase">
                Hikayemiz
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-6">
                Güvenin ve Kalitenin Adı
              </h2>
              <div className="text-gray-400 leading-relaxed whitespace-pre-line">
                {siteSettings?.aboutLong}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Satış Ofisimiz - YouTube Shorts Video */}
      <section className="py-24 bg-anthracite-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-gold text-sm font-semibold tracking-widest uppercase">
                Bizi Ziyaret Edin
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-6">
                Kemalpaşa Satış Ofisimiz
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Modern ve konforlu satış ofisimizde uzman ekibimiz sizi bekliyor. 
                Projelerimizi yakından inceleyebilir, detaylı bilgi alabilir ve 
                hayalinizdeki evi birlikte planlamaya başlayabiliriz.
              </p>
              <div className="flex items-center space-x-3 text-gray-400 mb-8">
                <MapPin className="w-5 h-5 text-gold" />
                <span>Kemalpaşa, İzmir</span>
              </div>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-gold text-anthracite font-semibold rounded hover:bg-gold-light transition-all duration-300"
              >
                Randevu Al
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
            <div className="order-1 lg:order-2">
              <YouTubeEmbed 
                url="https://youtube.com/shorts/rcVc3Y_x1Ac"
                title="Kemalpaşa Satış Ofisimiz"
                label="Satış Ofisimiz"
                isShorts={true}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-gold text-sm font-semibold tracking-widest uppercase">
              Rakamlarla Biz
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Başarılarımız
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center p-8 bg-anthracite-light rounded-lg border border-gold/10 hover:border-gold/30 transition-colors duration-300">
              <span className="text-4xl md:text-5xl font-bold text-gold">
                {siteSettings?.stats?.experience}
              </span>
              <p className="text-gray-400 mt-2">Yıllık Tecrübe</p>
            </div>
            <div className="text-center p-8 bg-anthracite-light rounded-lg border border-gold/10 hover:border-gold/30 transition-colors duration-300">
              <span className="text-4xl md:text-5xl font-bold text-gold">
                {siteSettings?.stats?.completedProjects}
              </span>
              <p className="text-gray-400 mt-2">Tamamlanan Proje</p>
            </div>
            <div className="text-center p-8 bg-anthracite-light rounded-lg border border-gold/10 hover:border-gold/30 transition-colors duration-300">
              <span className="text-4xl md:text-5xl font-bold text-gold">
                {siteSettings?.stats?.totalUnits}
              </span>
              <p className="text-gray-400 mt-2">Daire & Villa</p>
            </div>
            <div className="text-center p-8 bg-anthracite-light rounded-lg border border-gold/10 hover:border-gold/30 transition-colors duration-300">
              <span className="text-4xl md:text-5xl font-bold text-gold">
                {siteSettings?.stats?.happyFamilies}
              </span>
              <p className="text-gray-400 mt-2">Mutlu Aile</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-anthracite-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-gold text-sm font-semibold tracking-widest uppercase">
              Değerlerimiz
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Bizi Farklı Kılan
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="p-8 bg-anthracite rounded-lg border border-gold/10 hover:border-gold/30 transition-all duration-300 group"
              >
                <value.icon className="w-12 h-12 text-gold mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Projelerimizi Keşfedin
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Yıllardır gerçekleştirdiğimiz projelerimizi inceleyerek kalitemizi yakından görün.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/projeler"
              className="px-8 py-4 bg-gold text-anthracite font-semibold rounded hover:bg-gold-light transition-all duration-300 hover:shadow-lg hover:shadow-gold/20 flex items-center space-x-2"
            >
              <span>Projeleri Gör</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 border border-gold text-gold font-semibold rounded hover:bg-gold/10 transition-all duration-300"
            >
              İletişime Geç
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
